import KamChatBox from "../KamChat/KamChatBox";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Slider,
  Stack,
  Divider,
  Chip,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Collapse
} from '@mui/material';
import Paper from '@mui/material/Paper';
import {
  Save as SaveIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import {
  fetchBrandPlanning,
  saveBrandPlanning,
  requestPlanningSuggestion
} from '../../api/brands';

const DEFAULT_CATEGORY_OPTIONS = [
  { id: 'tops_tshirt', label: 'T-Shirts' },
  { id: 'tops_hoodie', label: 'Hoodies' },
  { id: 'outerwear', label: 'Outerwear' },
  { id: 'bottoms_denim', label: 'Denim' },
  { id: 'bottoms_trousers', label: 'Trousers' },
  { id: 'dresses', label: 'Dresses' },
  { id: 'accessories', label: 'Accessories' },
  { id: 'footwear', label: 'Footwear' }
];

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const MONTH_OPTIONS = MONTH_LABELS.map((label, index) => ({
  value: index + 1,
  label
}));

const DEFAULT_DROP_CYCLE_SHARE = 1 / 6;

const generateMonthSequence = (start, end) => {
  const startMonth = Number.isFinite(start) ? ((start - 1 + 12) % 12) + 1 : 1;
  const endMonth = Number.isFinite(end) ? ((end - 1 + 12) % 12) + 1 : startMonth;
  const months = [];
  let current = startMonth;
  for (let i = 0; i < 12; i += 1) {
    months.push(current);
    if (current === endMonth) break;
    current = (current % 12) + 1;
  }
  return months;
};

const describeCycleWindow = (cycle) => {
  if (!cycle || !Array.isArray(cycle.months) || cycle.months.length === 0) {
    return '';
  }
  return cycle.months
    .map((month) => MONTH_LABELS[((Number(month) || 1) - 1 + 12) % 12])
    .join(' • ');
};

const DEFAULT_DROP_CYCLES = [
  {
    cycleId: 'B1',
    label: 'December – January',
    startMonth: 12,
    endMonth: 1,
    months: [12, 1],
    season: 'Holiday / Resort',
    theme: 'Holiday Capsule',
    notes: '',
    launchOffsetDays: 0,
    shipOffsetDays: 45,
    channelFocus: {
      directToConsumer: 0.7,
      wholesale: 0.3
    },
    milestones: {
      designFreezeDays: -60,
      sampleDueDays: -30,
      marketingAssetDays: -20,
      launchDays: 0,
      shipDays: 45
    },
    meta: {},
    revenueShare: DEFAULT_DROP_CYCLE_SHARE
  },
  {
    cycleId: 'B2',
    label: 'February – March',
    startMonth: 2,
    endMonth: 3,
    months: [2, 3],
    season: 'Late Winter / Spring Preview',
    theme: 'Pre-Spring Drop',
    notes: '',
    launchOffsetDays: 0,
    shipOffsetDays: 45,
    channelFocus: {
      directToConsumer: 0.65,
      wholesale: 0.35
    },
    milestones: {
      designFreezeDays: -60,
      sampleDueDays: -30,
      marketingAssetDays: -20,
      launchDays: 0,
      shipDays: 45
    },
    meta: {},
    revenueShare: DEFAULT_DROP_CYCLE_SHARE
  },
  {
    cycleId: 'B3',
    label: 'April – May',
    startMonth: 4,
    endMonth: 5,
    months: [4, 5],
    season: 'Spring / Early Summer',
    theme: 'Spring Refresh',
    notes: '',
    launchOffsetDays: 0,
    shipOffsetDays: 45,
    channelFocus: {
      directToConsumer: 0.6,
      wholesale: 0.4
    },
    milestones: {
      designFreezeDays: -60,
      sampleDueDays: -30,
      marketingAssetDays: -20,
      launchDays: 0,
      shipDays: 45
    },
    meta: {},
    revenueShare: DEFAULT_DROP_CYCLE_SHARE
  },
  {
    cycleId: 'B4',
    label: 'June – July',
    startMonth: 6,
    endMonth: 7,
    months: [6, 7],
    season: 'High Summer',
    theme: 'Heatwave Capsule',
    notes: '',
    launchOffsetDays: 0,
    shipOffsetDays: 45,
    channelFocus: {
      directToConsumer: 0.55,
      wholesale: 0.45
    },
    milestones: {
      designFreezeDays: -60,
      sampleDueDays: -30,
      marketingAssetDays: -20,
      launchDays: 0,
      shipDays: 45
    },
    meta: {},
    revenueShare: DEFAULT_DROP_CYCLE_SHARE
  },
  {
    cycleId: 'B5',
    label: 'August – September',
    startMonth: 8,
    endMonth: 9,
    months: [8, 9],
    season: 'Pre-Fall',
    theme: 'Transitional Drop',
    notes: '',
    launchOffsetDays: 0,
    shipOffsetDays: 45,
    channelFocus: {
      directToConsumer: 0.5,
      wholesale: 0.5
    },
    milestones: {
      designFreezeDays: -60,
      sampleDueDays: -30,
      marketingAssetDays: -20,
      launchDays: 0,
      shipDays: 45
    },
    meta: {},
    revenueShare: DEFAULT_DROP_CYCLE_SHARE
  },
  {
    cycleId: 'B6',
    label: 'October – November',
    startMonth: 10,
    endMonth: 11,
    months: [10, 11],
    season: 'Fall / Early Holiday',
    theme: 'Fall Capsule',
    notes: '',
    launchOffsetDays: 0,
    shipOffsetDays: 45,
    channelFocus: {
      directToConsumer: 0.65,
      wholesale: 0.35
    },
    milestones: {
      designFreezeDays: -60,
      sampleDueDays: -30,
      marketingAssetDays: -20,
      launchDays: 0,
      shipDays: 45
    },
    meta: {},
    revenueShare: DEFAULT_DROP_CYCLE_SHARE
  }
];

const sanitizeDropCycle = (cycle, fallback, index) => {
  const base = fallback
    ? { ...fallback }
    : {
        cycleId: `CYCLE-${index + 1}`,
        label: 'Unnamed Cycle',
        startMonth: 1,
        endMonth: 2,
        months: [1, 2],
        season: '',
        theme: '',
    notes: '',
    launchOffsetDays: 0,
    shipOffsetDays: 45,
    milestones: {
      designFreezeDays: null,
      sampleDueDays: null,
      marketingAssetDays: null,
      launchDays: null,
      shipDays: null
    },
    channelFocus: {
      directToConsumer: 0.5,
      wholesale: 0.5
    },
    meta: {},
    revenueShare: 0
  };

  const next = { ...base };
  const incomingId = cycle?.cycleId ? String(cycle.cycleId).trim() : base.cycleId;
  next.cycleId = incomingId || base.cycleId;
  if (cycle?.label) next.label = String(cycle.label);
  const startMonth = Number.isFinite(Number(cycle?.startMonth))
    ? Math.min(Math.max(Number(cycle.startMonth), 1), 12)
    : next.startMonth;
  const endMonth = Number.isFinite(Number(cycle?.endMonth))
    ? Math.min(Math.max(Number(cycle.endMonth), 1), 12)
    : next.endMonth;
  next.startMonth = startMonth;
  next.endMonth = endMonth;
  if (Array.isArray(cycle?.months) && cycle.months.length > 0) {
    next.months = cycle.months
      .map((month) => Math.min(Math.max(Number(month) || 1, 1), 12))
      .filter((month, idx, arr) => arr.indexOf(month) === idx);
  } else {
    next.months = generateMonthSequence(startMonth, endMonth);
  }
  next.season = cycle?.season ? String(cycle.season) : next.season;
  next.theme = cycle?.theme ? String(cycle.theme) : next.theme;
  next.notes = cycle?.notes ? String(cycle.notes) : next.notes;
  next.launchOffsetDays = Number.isFinite(Number(cycle?.launchOffsetDays))
    ? Number(cycle.launchOffsetDays)
    : next.launchOffsetDays;
  next.shipOffsetDays = Number.isFinite(Number(cycle?.shipOffsetDays))
    ? Number(cycle.shipOffsetDays)
    : next.shipOffsetDays;

  const incomingMilestones = cycle?.milestones || {};
  next.milestones = {
    designFreezeDays: Number.isFinite(Number(incomingMilestones.designFreezeDays))
      ? Number(incomingMilestones.designFreezeDays)
      : next.milestones.designFreezeDays,
    sampleDueDays: Number.isFinite(Number(incomingMilestones.sampleDueDays))
      ? Number(incomingMilestones.sampleDueDays)
      : next.milestones.sampleDueDays,
    marketingAssetDays: Number.isFinite(Number(incomingMilestones.marketingAssetDays))
      ? Number(incomingMilestones.marketingAssetDays)
      : next.milestones.marketingAssetDays,
    launchDays: Number.isFinite(Number(incomingMilestones.launchDays))
      ? Number(incomingMilestones.launchDays)
      : next.milestones.launchDays,
    shipDays: Number.isFinite(Number(incomingMilestones.shipDays))
      ? Number(incomingMilestones.shipDays)
      : next.milestones.shipDays
  };

  const incomingChannelFocus = cycle?.channelFocus || {};
  if (
    Number.isFinite(Number(incomingChannelFocus.directToConsumer)) ||
    Number.isFinite(Number(incomingChannelFocus.wholesale))
  ) {
    const direct =
      Number.isFinite(Number(incomingChannelFocus.directToConsumer)) &&
      Number(incomingChannelFocus.directToConsumer) >= 0
        ? Math.min(Number(incomingChannelFocus.directToConsumer), 1)
        : null;
    const wholesale =
      Number.isFinite(Number(incomingChannelFocus.wholesale)) &&
      Number(incomingChannelFocus.wholesale) >= 0
        ? Math.min(Number(incomingChannelFocus.wholesale), 1)
        : null;

    if (direct !== null) {
      next.channelFocus.directToConsumer = direct;
      next.channelFocus.wholesale =
        wholesale !== null ? wholesale : Math.max(0, 1 - direct);
    } else if (wholesale !== null) {
      next.channelFocus.wholesale = wholesale;
      next.channelFocus.directToConsumer = Math.max(0, 1 - wholesale);
    }
  }

  const incomingShare = Number.isFinite(Number(cycle?.revenueShare))
    ? Math.min(Math.max(Number(cycle.revenueShare), 0), 1)
    : Number.isFinite(Number(next.revenueShare))
    ? Math.min(Math.max(Number(next.revenueShare), 0), 1)
    : 0;
  next.revenueShare = incomingShare;

  next.meta = { ...(cycle?.meta || next.meta) };

  const incomingProductMix = Array.isArray(cycle?.productMix) ? cycle.productMix : [];
  next.productMix = incomingProductMix.map((item) => {
    const plannedLooks = Number.isFinite(Number(item?.plannedLooks))
      ? Math.max(Number(item.plannedLooks), 0)
      : Number.isFinite(Number(item?.plannedUnits))
      ? Math.max(Number(item.plannedUnits), 0)
      : 0;

    const plannedUnits = Number.isFinite(Number(item?.plannedUnits))
      ? Math.max(Number(item.plannedUnits), 0)
      : 0;

    return {
      categoryId: item?.categoryId || null,
      categoryLabel: item?.categoryLabel || '',
      plannedShare: Number.isFinite(Number(item?.plannedShare)) ? Number(item.plannedShare) : 0,
      plannedLooks,
      plannedUnits,
      manualPlannedLooks: Boolean(item?.manualPlannedLooks),
      manualPlannedUnits: Boolean(item?.manualPlannedUnits),
      averagePrice: Number.isFinite(Number(item?.averagePrice)) ? Number(item.averagePrice) : 0,
      averageCogs: Number.isFinite(Number(item?.averageCogs)) ? Number(item.averageCogs) : 0,
      averageUnitPrice: Number.isFinite(Number(item?.averageUnitPrice)) ? Number(item.averageUnitPrice) : 0,
      averageUnitCogs: Number.isFinite(Number(item?.averageUnitCogs)) ? Number(item.averageUnitCogs) : 0,
      notes: item?.notes || ''
    };
  });

  return next;
};

const defaultPlanningState = () => ({
  status: 'draft',
  summary: {
    plannedRevenue: 0,
    targetMargin: 0,
    plannedLooks: 0,
    productCount: 0,
    projectedUnits: 0
  },
  financialPlan: {
    revenueGoal: 0,
    marginTarget: 0,
    channelSplit: {
      directToConsumer: 1,
      wholesale: 0
    },
    campaignLengthWeeks: 8,
    assumptions: '',
    costStructure: {
      materialsShare: 0.6
    },
    plannedDiscountRate: 0
  },
  productMix: [],
  timeline: [],
  assumptions: '',
  lmHistory: [],
  updatedAt: null,
  updatedBy: null,
  dropCycles: DEFAULT_DROP_CYCLES.map((cycle) => ({ ...cycle, months: [...cycle.months] }))
});

const ensurePlanningDefaults = (planning) => {
  const defaults = defaultPlanningState();
  const source = planning && typeof planning === 'object' ? planning : {};

  const { budget: _deprecatedBudget, ...incomingFinancialPlan } = source.financialPlan || {};

  return {
    ...defaults,
    ...source,
    summary: {
      ...defaults.summary,
      ...(source.summary || {}),
      plannedLooks: Number.isFinite(source.summary?.plannedLooks)
        ? Math.max(source.summary.plannedLooks, 0)
        : Number.isFinite(source.summary?.productCount)
        ? Math.max(source.summary.productCount, 0)
        : defaults.summary.plannedLooks,
      productCount: Number.isFinite(source.summary?.productCount)
        ? Math.max(source.summary.productCount, 0)
        : Number.isFinite(source.summary?.plannedLooks)
        ? Math.max(source.summary.plannedLooks, 0)
        : defaults.summary.productCount
    },
    financialPlan: {
      ...defaults.financialPlan,
      ...incomingFinancialPlan,
      channelSplit: {
        ...defaults.financialPlan.channelSplit,
        ...(source.financialPlan?.channelSplit || {})
      },
    costStructure: {
      materialsShare: Number.isFinite(source.financialPlan?.costStructure?.materialsShare)
        ? Math.min(Math.max(source.financialPlan.costStructure.materialsShare, 0), 1)
        : defaults.financialPlan.costStructure.materialsShare
    },
    plannedDiscountRate: Number.isFinite(source.financialPlan?.plannedDiscountRate)
      ? Math.min(Math.max(source.financialPlan.plannedDiscountRate, 0), 1)
      : defaults.financialPlan.plannedDiscountRate
  },
    productMix: Array.isArray(source.productMix)
      ? source.productMix.map((item) => {
          const plannedLooks = Number.isFinite(item.plannedLooks)
            ? Math.max(item.plannedLooks, 0)
            : Number.isFinite(item.plannedUnits)
            ? Math.max(item.plannedUnits, 0)
            : 0;

          const plannedUnits = Number.isFinite(item.plannedUnits) ? Math.max(item.plannedUnits, 0) : 0;

          return {
            categoryId: item.categoryId || null,
            categoryLabel: item.categoryLabel || '',
            plannedShare: Number.isFinite(item.plannedShare) ? Number(item.plannedShare) : 0,
            plannedLooks,
            plannedUnits,
            manualPlannedLooks: Boolean(item.manualPlannedLooks),
            manualPlannedUnits: Boolean(item.manualPlannedUnits),
            averagePrice: Number.isFinite(item.averagePrice) ? Number(item.averagePrice) : 0,
            averageCogs: Number.isFinite(item.averageCogs) ? Number(item.averageCogs) : 0,
            averageUnitPrice: Number.isFinite(item.averageUnitPrice) ? Number(item.averageUnitPrice) : 0,
            averageUnitCogs: Number.isFinite(item.averageUnitCogs) ? Number(item.averageUnitCogs) : 0,
            notes: item.notes || ''
          };
        })
      : [],
    timeline: Array.isArray(source.timeline) ? source.timeline.map((item) => ({
      milestoneId: item.milestoneId || null,
      label: item.label || '',
      dueDate: item.dueDate ? new Date(item.dueDate).toISOString().slice(0, 10) : '',
      owner: item.owner || '',
      dependencies: Array.isArray(item.dependencies) ? item.dependencies : [],
      status: ['pending', 'in_progress', 'done'].includes(item.status) ? item.status : 'pending'
    })) : [],
    assumptions: source.assumptions || '',
    lmHistory: Array.isArray(source.lmHistory) ? source.lmHistory : [],
    updatedAt: source.updatedAt ? new Date(source.updatedAt) : null,
    updatedBy: source.updatedBy || null,
    dropCycles: (() => {
      const incoming = Array.isArray(source.dropCycles) ? source.dropCycles : [];
      const sanitizedIncoming = incoming.map((cycle, index) =>
        sanitizeDropCycle(
          cycle,
          DEFAULT_DROP_CYCLES.find((defaultCycle) => defaultCycle.cycleId === cycle?.cycleId),
          index
        )
      );

      const existingIds = new Set(
        sanitizedIncoming.map((cycle) => cycle.cycleId).filter(Boolean)
      );

      const fallbackCycles = DEFAULT_DROP_CYCLES.filter(
        (cycle) => !existingIds.has(cycle.cycleId)
      ).map((cycle, index) =>
        sanitizeDropCycle(cycle, cycle, sanitizedIncoming.length + index)
      );

      return [...sanitizedIncoming, ...fallbackCycles];
    })()
  };
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value >= 1000 ? 0 : 2
  }).format(Number.isFinite(value) ? value : 0);

const formatPercent = (value) =>
  `${Math.round((Number.isFinite(value) ? value : 0) * 100)}%`;

const SummaryMetricCard = ({ title, value, helper, onEdit, titleTooltip }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {titleTooltip && (
              <Tooltip title={titleTooltip}>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </Box>
              </Tooltip>
            )}
          </Box>
          <Typography variant="h5" sx={{ mt: 0.5 }}>
            {value}
          </Typography>
          {helper && (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          )}
        </Box>
        {onEdit && (
          <Tooltip title="Jump to section">
            <IconButton size="small" onClick={onEdit}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </CardContent>
  </Card>
);

const deepMerge = (target, patch) => {
  if (patch === null || patch === undefined) {
    return target;
  }

  if (Array.isArray(patch)) {
    return patch;
  }

  if (typeof patch !== 'object') {
    return patch;
  }

  const result = Array.isArray(target) ? [...target] : { ...target };

  Object.entries(patch).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = deepMerge(target ? target[key] : undefined, value);
    } else {
      result[key] = value;
    }
  });

  return result;
};

const BrandPlanningTab = ({ brandId, variant = 'full' }) => {
  const [planning, setPlanning] = useState(defaultPlanningState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [dirty, setDirty] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [showVideoGuide, setShowVideoGuide] = useState(false);

  const financialRef = useRef(null);
  const productMixRef = useRef(null);
  const timelineRef = useRef(null);
  const aiPromptRef = useRef(null);

  const historyEntries = useMemo(() => {
    if (!Array.isArray(planning.lmHistory)) {
      return [];
    }
    return [...planning.lmHistory].sort(
      (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
    );
  }, [planning.lmHistory]);

  useEffect(() => {
    const loadPlanning = async () => {
      if (!brandId) return;
      setLoading(true);
      setErrorMessages([]);
      try {
        const data = await fetchBrandPlanning(brandId, { includeHistory: true });
        setPlanning(ensurePlanningDefaults(data));
        setDirty(false);
      } catch (error) {
        console.error('Failed to load planning data', error);
        setErrorMessages(['Failed to load planning data.']);
      } finally {
        setLoading(false);
      }
    };

    loadPlanning();
  }, [brandId]);

  // --- KAM: Build planner context ---------------------------------
const plannerContext = planning
? {
    status: planning.status,
    summary: planning.summary,
    financialPlan: planning.financialPlan,
    productMix: planning.productMix,
    dropCycles: planning.dropCycles,
    assumptions: planning.assumptions,
    timeline: planning.timeline,
    planningDocId: brandId,
    updatedAt: planning.updatedAt
  }
: null;

  const handleSummaryChange = (field, value) => {
    setPlanning((prev) => {
      const isLookField = field === 'plannedLooks' || field === 'productCount';
      const isUnitField = field === 'projectedUnits';
      const normalizedField = field === 'productCount' ? 'plannedLooks' : field;

      const numericValue = isLookField || isUnitField
        ? Math.max(0, Math.round(Number(value) || 0))
        : value;

      let nextMix = prev.productMix ? [...prev.productMix] : [];
      if (nextMix.length > 0 && (isLookField || isUnitField)) {
        const shareTotal = nextMix.reduce(
          (sum, item) => sum + (Number.isFinite(item.plannedShare) ? Math.max(item.plannedShare, 0) : 0),
          0
        );
        const categoryCount = nextMix.length || 1;

        if (isLookField) {
          const totalLooks = numericValue;
          nextMix = nextMix.map((item) => {
            if (item.manualPlannedLooks) {
              return item;
            }

            const sharePortion =
              shareTotal > 0
                ? (Number.isFinite(item.plannedShare) ? Math.max(item.plannedShare, 0) : 0) / (shareTotal || 1)
                : 1 / categoryCount;

            return {
              ...item,
              plannedLooks: totalLooks > 0 ? Math.max(0, Math.round(totalLooks * sharePortion)) : 0
            };
          });
        }

        if (isUnitField) {
          const totalUnits = numericValue;
          nextMix = nextMix.map((item) => {
            if (item.manualPlannedUnits) {
              return item;
            }

            const sharePortion =
              shareTotal > 0
                ? (Number.isFinite(item.plannedShare) ? Math.max(item.plannedShare, 0) : 0) / (shareTotal || 1)
                : 1 / categoryCount;

            return {
              ...item,
              plannedUnits: totalUnits > 0 ? Math.max(0, Math.round(totalUnits * sharePortion)) : 0
            };
          });
        }
      }

      return {
        ...prev,
        summary: {
          ...prev.summary,
          plannedLooks:
            isLookField
              ? numericValue
              : prev.summary.plannedLooks,
          productCount:
            isLookField
              ? numericValue
              : prev.summary.productCount,
          projectedUnits: isUnitField ? numericValue : prev.summary.projectedUnits,
          [normalizedField]: numericValue
        },
        productMix: nextMix
      };
    });
    setDirty(true);
  };

  const handleFinancialChange = (field, value) => {
    setPlanning((prev) => ({
      ...prev,
      financialPlan: {
        ...prev.financialPlan,
        [field]: value
      },
      summary:
        field === 'revenueGoal'
          ? { ...prev.summary, plannedRevenue: value }
          : field === 'marginTarget'
          ? { ...prev.summary, targetMargin: value }
          : { ...prev.summary }
    }));
    setDirty(true);
  };

  const handleChannelSplitChange = (directPercentage) => {
    const ratio = Math.min(Math.max(directPercentage / 100, 0), 1);
    setPlanning((prev) => ({
      ...prev,
      financialPlan: {
        ...prev.financialPlan,
        channelSplit: {
          directToConsumer: ratio,
          wholesale: Math.max(0, 1 - ratio)
        }
      }
    }));
    setDirty(true);
  };

  const handleProductMixChange = (index, field, value) => {
    setPlanning((prev) => {
      let nextMix = prev.productMix ? [...prev.productMix] : [];
      if (!nextMix[index]) return prev;

      if (field === 'plannedShare') {
        const shareValue = Math.min(Math.max(Number(value) || 0, 0), 1);
        nextMix[index] = {
          ...nextMix[index],
          plannedShare: shareValue
        };

        const shareTotal = nextMix.reduce(
          (sum, item) => sum + (Number.isFinite(item.plannedShare) ? Math.max(item.plannedShare, 0) : 0),
          0
        );
        const categoryCount = nextMix.length || 1;
        const totalLooks = prev.summary.plannedLooks || prev.summary.productCount || categoryCount || 0;
        const totalUnits = prev.summary.projectedUnits || 0;

        nextMix = nextMix.map((item) => {
          const sharePortion =
            shareTotal > 0
              ? (Number.isFinite(item.plannedShare) ? Math.max(item.plannedShare, 0) : 0) / (shareTotal || 1)
              : 1 / categoryCount;

          return {
            ...item,
            plannedLooks: item.manualPlannedLooks
              ? item.plannedLooks
              : Math.max(0, Math.round(totalLooks * sharePortion)),
            plannedUnits: item.manualPlannedUnits
              ? item.plannedUnits
              : Math.max(0, Math.round(totalUnits * sharePortion))
          };
        });
      } else {
        nextMix[index] = {
          ...nextMix[index],
          [field]: value
        };
      }

      return {
        ...prev,
        productMix: nextMix
      };
    });
    setDirty(true);
  };

  const handleAddCategory = () => {
    setPlanning((prev) => ({
      ...prev,
      productMix: [
        ...(prev.productMix || []),
        {
          categoryId: null,
          categoryLabel: '',
          plannedShare: 0,
          plannedLooks: 0,
          plannedUnits: 0,
          manualPlannedLooks: false,
          manualPlannedUnits: false,
          averagePrice: 0,
          averageCogs: 0,
          notes: ''
        }
      ],
      summary: {
        ...prev.summary,
        plannedLooks: (prev.productMix?.length || 0) + 1,
        productCount: (prev.productMix?.length || 0) + 1
      }
    }));
    setDirty(true);
  };

  const handleRemoveCategory = (index) => {
    setPlanning((prev) => {
      const nextMix = [...(prev.productMix || [])];
      nextMix.splice(index, 1);
      return {
        ...prev,
        productMix: nextMix
      };
    });
    setDirty(true);
  };

  const handlePlannedLooksChange = (index, value) => {
    const isClearing = value === '' || value === null;
    const nextUnits = isClearing ? 0 : Math.max(0, Math.round(Number(value) || 0));
    setPlanning((prev) => {
      const nextMix = prev.productMix ? [...prev.productMix] : [];
      if (!nextMix[index]) return prev;

      nextMix[index] = {
        ...nextMix[index],
        plannedLooks: nextUnits,
        manualPlannedLooks: !isClearing
      };

      return {
        ...prev,
        productMix: nextMix
      };
    });
    setDirty(true);
  };

  const handlePlannedUnitsChange = (index, value) => {
    const isClearing = value === '' || value === null;
    const nextUnits = isClearing ? 0 : Math.max(0, Math.round(Number(value) || 0));
    setPlanning((prev) => {
      const nextMix = prev.productMix ? [...prev.productMix] : [];
      if (!nextMix[index]) return prev;

      nextMix[index] = {
        ...nextMix[index],
        plannedUnits: nextUnits,
        manualPlannedUnits: !isClearing
      };

      return {
        ...prev,
        productMix: nextMix
      };
    });
    setDirty(true);
  };

  const handleTimelineChange = (index, field, value) => {
    setPlanning((prev) => {
      const nextTimeline = prev.timeline ? [...prev.timeline] : [];
      nextTimeline[index] = {
        ...nextTimeline[index],
        [field]: field === 'dueDate' ? value : value
      };
      return {
        ...prev,
        timeline: nextTimeline
      };
    });
    setDirty(true);
  };

  const handleAddMilestone = () => {
    setPlanning((prev) => ({
      ...prev,
      timeline: [
        ...(prev.timeline || []),
        {
          milestoneId: null,
          label: '',
          dueDate: '',
          owner: '',
          dependencies: [],
          status: 'pending'
        }
      ]
    }));
    setDirty(true);
  };

  const handleRemoveMilestone = (index) => {
    setPlanning((prev) => {
      const nextTimeline = [...(prev.timeline || [])];
      nextTimeline.splice(index, 1);
      return {
        ...prev,
        timeline: nextTimeline
      };
    });
    setDirty(true);
  };

  const handleDropCycleFieldChange = (index, field, rawValue) => {
    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      if (!cycles[index]) {
        return prev;
      }

      const nextCycle = { ...cycles[index] };
      const numericFields = new Set([
        'startMonth',
        'endMonth',
        'launchOffsetDays',
        'shipOffsetDays'
      ]);

      const value = numericFields.has(field)
        ? Math.round(Number(rawValue) || 0)
        : rawValue;

      nextCycle[field] = value;

      if (field === 'startMonth' || field === 'endMonth') {
        const start = field === 'startMonth' ? value : nextCycle.startMonth;
        const end = field === 'endMonth' ? value : nextCycle.endMonth;
        const safeStart = Number.isFinite(start) ? Math.min(Math.max(start, 1), 12) : 1;
        const safeEnd = Number.isFinite(end) ? Math.min(Math.max(end, 1), 12) : safeStart;
        nextCycle.startMonth = safeStart;
        nextCycle.endMonth = safeEnd;
        nextCycle.months = generateMonthSequence(safeStart, safeEnd);
      }

      cycles[index] = nextCycle;
      return {
        ...prev,
        dropCycles: cycles
      };
    });
    setDirty(true);
  };

  const handleDropCycleMilestoneChange = (index, key, rawValue) => {
    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      if (!cycles[index]) {
        return prev;
      }

      const nextCycle = { ...cycles[index], milestones: { ...(cycles[index].milestones || {}) } };
      const value = rawValue === '' || rawValue === null ? null : Math.round(Number(rawValue) || 0);
      nextCycle.milestones[key] = value;
      cycles[index] = nextCycle;
      return {
        ...prev,
        dropCycles: cycles
      };
    });
    setDirty(true);
  };

  const handleDropCycleChannelChange = (index, ratioPercent) => {
    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      if (!cycles[index]) {
        return prev;
      }

      const ratio = Math.min(Math.max(ratioPercent / 100, 0), 1);
      cycles[index] = {
        ...cycles[index],
        channelFocus: {
          directToConsumer: ratio,
          wholesale: Math.max(0, 1 - ratio)
        }
      };

      return {
        ...prev,
        dropCycles: cycles
      };
    });
    setDirty(true);
  };

  const handleDropCycleRevenueShareChange = (index, rawValue) => {
    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      if (!cycles[index]) {
        return prev;
      }

      const nextCycle = { ...cycles[index] };
      const percent = Number(rawValue);
      const boundedPercent = Number.isFinite(percent)
        ? Math.min(Math.max(percent, 0), 100)
        : 0;
      const share = boundedPercent / 100;
      nextCycle.revenueShare = Number.isFinite(share) ? share : 0;
      cycles[index] = nextCycle;
      return {
        ...prev,
        dropCycles: cycles
      };
    });
    setDirty(true);
  };

  const handleAddDropCycle = () => {
    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      const template = DEFAULT_DROP_CYCLES[cycles.length % DEFAULT_DROP_CYCLES.length] || DEFAULT_DROP_CYCLES[0];
      const nextCycle = {
        ...template,
        cycleId: `${template?.cycleId || 'CYCLE'}_${cycles.length + 1}`,
        months: [...(template?.months || [])]
      };
      return {
        ...prev,
        dropCycles: [...cycles, nextCycle]
      };
    });
    setDirty(true);
  };

  const handleRemoveDropCycle = (index) => {
    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      if (!cycles[index]) {
        return prev;
      }
      cycles.splice(index, 1);
      return {
        ...prev,
        dropCycles: cycles
      };
    });
    setDirty(true);
  };

  const handleResetDropCycle = (index) => {
    const fallback = DEFAULT_DROP_CYCLES[index] || DEFAULT_DROP_CYCLES[0];
    if (!fallback) return;

    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      if (!cycles[index]) {
        return prev;
      }

      const preservedId = cycles[index].cycleId || fallback.cycleId;
      cycles[index] = {
        ...fallback,
        cycleId: preservedId,
        months: [...fallback.months]
      };

      return {
        ...prev,
        dropCycles: cycles
      };
    });
    setDirty(true);
  };

  const handleAddBimesterCategory = (cycleIndex) => {
    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      if (!cycles[cycleIndex]) return prev;

      const cycle = { ...cycles[cycleIndex] };
      const productMix = Array.isArray(cycle.productMix) ? [...cycle.productMix] : [];

      productMix.push({
        categoryId: null,
        categoryLabel: '',
        plannedShare: 0,
        plannedLooks: 0,
        manualPlannedLooks: false,
        plannedUnits: 0,
        manualPlannedUnits: false,
        averagePrice: 0,
        averageCogs: 0,
        averageUnitPrice: 0,
        averageUnitCogs: 0,
        notes: ''
      });

      cycle.productMix = productMix;
      cycles[cycleIndex] = cycle;

      return {
        ...prev,
        dropCycles: cycles
      };
    });
    setDirty(true);
  };

  const handleRemoveBimesterCategory = (cycleIndex, categoryIndex) => {
    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      if (!cycles[cycleIndex]) return prev;

      const cycle = { ...cycles[cycleIndex] };
      const productMix = Array.isArray(cycle.productMix) ? [...cycle.productMix] : [];

      productMix.splice(categoryIndex, 1);

      cycle.productMix = productMix;
      cycles[cycleIndex] = cycle;

      return {
        ...prev,
        dropCycles: cycles
      };
    });
    setDirty(true);
  };

  const handleBimesterProductMixChange = (cycleIndex, categoryIndex, field, value) => {
    setPlanning((prev) => {
      const cycles = Array.isArray(prev.dropCycles) ? [...prev.dropCycles] : [];
      if (!cycles[cycleIndex]) return prev;

      const cycle = { ...cycles[cycleIndex] };
      const productMix = Array.isArray(cycle.productMix) ? [...cycle.productMix] : [];

      if (!productMix[categoryIndex]) return prev;

      productMix[categoryIndex] = {
        ...productMix[categoryIndex],
        [field]: value
      };

      cycle.productMix = productMix;
      cycles[cycleIndex] = cycle;

      return {
        ...prev,
        dropCycles: cycles
      };
    });
    setDirty(true);
  };

  const revenueGoal = planning.financialPlan.revenueGoal || 0;
  const marginTarget = planning.financialPlan.marginTarget || 0;
  const plannedRevenue = planning.summary.plannedRevenue || revenueGoal;
  const projectedUnits = planning.summary.projectedUnits || 0;
  const plannedLooksTarget =
    planning.summary.plannedLooks || planning.summary.productCount || planning.productMix?.length || 0;
  const fallbackLooksBase = planning.productMix?.length || 0;
  const totalCogsBudget = revenueGoal * Math.max(0, 1 - marginTarget);
  const totalGrossMargin = revenueGoal * Math.max(0, marginTarget);
  const perLookCogsBudget = plannedLooksTarget > 0 ? totalCogsBudget / plannedLooksTarget : 0;
  const perLookGrossMargin = plannedLooksTarget > 0 ? totalGrossMargin / plannedLooksTarget : 0;
  const perUnitCogsBudget = projectedUnits > 0 ? totalCogsBudget / projectedUnits : 0;
  const perUnitGrossMargin = projectedUnits > 0 ? totalGrossMargin / projectedUnits : 0;
  const rawMaterialsShare =
    planning.financialPlan?.costStructure?.materialsShare !== undefined
      ? planning.financialPlan.costStructure.materialsShare
      : 0.6;
  const materialsShare = Math.min(Math.max(rawMaterialsShare, 0), 1);
  const laborShare = 1 - materialsShare;

  const handleCostStructureChange = (value) => {
    const normalized = Math.min(Math.max(value / 100, 0), 1);
    setPlanning((prev) => ({
      ...prev,
      financialPlan: {
        ...prev.financialPlan,
        costStructure: {
          ...(prev.financialPlan?.costStructure || {}),
          materialsShare: normalized
        }
      }
    }));
    setDirty(true);
  };

  const derivedProductMix = useMemo(() => {
    if (!Array.isArray(planning?.productMix)) return [];

    const shareSumRaw = planning.productMix.reduce(
      (sum, item) => sum + (Number.isFinite(item.plannedShare) ? Math.max(item.plannedShare, 0) : 0),
      0
    );

    const categoryCount = planning.productMix.length || 0;

    const totalLooksTarget =
      planning.summary?.plannedLooks ||
      planning.summary?.productCount ||
      categoryCount ||
      0;

    const totalUnitsTarget = planning.summary?.projectedUnits || projectedUnits || 0;

    return planning.productMix.map((item) => {
      const storedShare = Number.isFinite(item.plannedShare) ? Math.max(item.plannedShare, 0) : 0;
      const shareAllocation =
        shareSumRaw > 0
          ? storedShare / (shareSumRaw || 1)
          : categoryCount > 0
          ? 1 / categoryCount
          : 0;

      const manualLooks = Number.isFinite(item.plannedLooks) ? Math.max(item.plannedLooks, 0) : 0;
      const manualUnits = Number.isFinite(item.plannedUnits) ? Math.max(item.plannedUnits, 0) : 0;

      const hasManualLooks = Boolean(item.manualPlannedLooks);
      const hasManualUnits = Boolean(item.manualPlannedUnits);

      const autoPlannedLooks =
        totalLooksTarget > 0 ? Math.max(0, Math.round(totalLooksTarget * shareAllocation)) : 0;
      const autoPlannedUnits =
        totalUnitsTarget > 0 ? Math.max(0, Math.round(totalUnitsTarget * shareAllocation)) : 0;

      const plannedLooks = hasManualLooks ? manualLooks : autoPlannedLooks;
      const plannedUnits = hasManualUnits ? manualUnits : autoPlannedUnits;

      const categoryRevenue = plannedRevenue * shareAllocation;
      const categoryCogsBudget = totalCogsBudget * shareAllocation;

      const perLookRetail = plannedLooks > 0 ? categoryRevenue / plannedLooks : 0;
      const perLookCogs = plannedLooks > 0 ? categoryCogsBudget / plannedLooks : 0;
      const perUnitRevenue = plannedUnits > 0 ? categoryRevenue / plannedUnits : 0;
      const perUnitCogs = plannedUnits > 0 ? categoryCogsBudget / plannedUnits : 0;
      const materialsBudgetPerLook = perLookCogs * materialsShare;
      const laborBudgetPerLook = perLookCogs * laborShare;
      const materialsBudgetPerUnit = perUnitCogs * materialsShare;
      const laborBudgetPerUnit = perUnitCogs * laborShare;
      const materialsBudgetTotal = categoryCogsBudget * materialsShare;
      const laborBudgetTotal = categoryCogsBudget * laborShare;

      return {
        ...item,
        share: storedShare,
        shareAllocation,
        plannedLooks,
        autoPlannedLooks,
        manualPlannedLooks: hasManualLooks,
        plannedUnits,
        autoPlannedUnits,
        manualPlannedUnits: hasManualUnits,
        categoryRevenue,
        categoryCogsBudget,
        perLookRetail,
        perLookCogs,
        perUnitRevenue,
        perUnitCogs,
        materialsBudgetPerLook,
        laborBudgetPerLook,
        materialsBudgetPerUnit,
        laborBudgetPerUnit,
        materialsBudgetTotal,
        laborBudgetTotal
      };
    });
  }, [
    planning.productMix,
    planning.summary?.plannedLooks,
    planning.summary?.productCount,
    planning.summary?.projectedUnits,
    plannedRevenue,
    totalCogsBudget,
    materialsShare,
    laborShare,
    projectedUnits
  ]);

  const derivedLooksTotal = useMemo(
    () => derivedProductMix.reduce((sum, item) => sum + (item.plannedLooks || 0), 0),
    [derivedProductMix]
  );

  const derivedUnitsTotal = useMemo(
    () => derivedProductMix.reduce((sum, item) => sum + (item.plannedUnits || 0), 0),
    [derivedProductMix]
  );

  const looksMismatch =
    planning.summary?.plannedLooks > 0
      ? planning.summary.plannedLooks - derivedLooksTotal
      : 0;
  const hasLooksMismatch =
    planning.summary?.plannedLooks > 0 && looksMismatch !== 0;

  const unitsMismatch =
    planning.summary?.projectedUnits > 0
      ? planning.summary.projectedUnits - derivedUnitsTotal
      : 0;
  const hasUnitsMismatch =
    planning.summary?.projectedUnits > 0 && unitsMismatch !== 0;

  const validatePlan = (plan) => {
    const issues = [];
    const { financialPlan, productMix } = plan;

    if (financialPlan.marginTarget < 0 || financialPlan.marginTarget > 1) {
      issues.push('Target margin must be between 0% and 100%.');
    }

    if (financialPlan.revenueGoal < 0) {
      issues.push('Revenue goal must be positive.');
    }

    const mixTotal = (productMix || []).reduce((sum, item) => sum + (item.plannedShare || 0), 0);
    if (mixTotal > 1.25) {
      issues.push('Product mix share exceeds 125%.');
    }

    const shareSum = (plan.dropCycles || []).reduce(
      (sum, cycle) =>
        sum + (Number.isFinite(cycle.revenueShare) ? Math.max(cycle.revenueShare, 0) : 0),
      0
    );
    if (Math.abs(shareSum - 1) > 0.01) {
      issues.push('Revenue share across drop cycles must total 100%.');
    }

    return issues;
  };

  const handleSave = async (nextStatus = 'draft') => {
    if (!brandId) return;
    const normalizedMix = derivedProductMix.map((item) => ({
      categoryId: item.categoryId,
      categoryLabel: item.categoryLabel,
      plannedShare: item.share,
      plannedLooks: item.plannedLooks,
      manualPlannedLooks: Boolean(item.manualPlannedLooks),
      plannedUnits: item.plannedUnits,
      manualPlannedUnits: Boolean(item.manualPlannedUnits),
      averagePrice: item.perLookRetail,
      averageCogs: item.perLookCogs,
      averageUnitPrice: item.perUnitRevenue,
      averageUnitCogs: item.perUnitCogs,
      notes: item.notes || ''
    }));

    const planToSave = ensurePlanningDefaults({
      ...planning,
      status: nextStatus,
      productMix: normalizedMix
    });

    planToSave.summary.productCount = planToSave.summary.productCount || planToSave.summary.plannedLooks;

    const validationIssues = validatePlan(planToSave);
    if (validationIssues.length > 0) {
      setErrorMessages(validationIssues);
      toast.error('Please resolve validation errors before saving.');
      return;
    }

    setSaving(true);
    setErrorMessages([]);

    try {
      const saved = await saveBrandPlanning(brandId, planToSave);
      setPlanning(ensurePlanningDefaults(saved));
      setDirty(false);
      toast.success(nextStatus === 'committed' ? 'Plan committed' : 'Draft saved');
    } catch (error) {
      console.error('Failed to save planning data', error);
      setErrorMessages(['Failed to save planning data.']);
      toast.error('Failed to save planning data');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestSuggestion = async () => {
    if (!prompt.trim()) {
      toast.error('Add a question or note before asking the assistant.');
      return;
    }

    setAiLoading(true);
    setErrorMessages([]);
    try {
      const { suggestion, planning: updatedPlanning } = await requestPlanningSuggestion(brandId, {
        prompt
      });

      if (suggestion?.narrative) {
        toast.success('AI suggestion ready');
      }

      setPlanning(ensurePlanningDefaults(updatedPlanning));
      setPrompt('');
      setDirty(true);
    } catch (error) {
      console.error('Failed to fetch planning suggestion', error);
      setErrorMessages(['Failed to fetch planning suggestion.']);
      toast.error('AI suggestion failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplySuggestion = (entry) => {
    if (!entry?.suggestedChanges) {
      return;
    }

    setPlanning((prev) => {
      const mergedPlan = deepMerge(prev, entry.suggestedChanges);
      const updatedHistory = (prev.lmHistory || []).map((historyEntry) => {
        if (historyEntry.timestamp === entry.timestamp) {
          return {
            ...historyEntry,
            applied: true
          };
        }
        return historyEntry;
      });

      return {
        ...ensurePlanningDefaults(mergedPlan),
        lmHistory: updatedHistory
      };
    });
    setDirty(true);
  };

  const summaryCards = [
    {
      key: 'revenue',
      title: 'Planned Revenue',
      value: formatCurrency(planning.summary.plannedRevenue || planning.financialPlan.revenueGoal),
      helper: `Gross Profit: ${formatCurrency(totalGrossMargin)}`,
      onEdit: () => financialRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    {
      key: 'margin',
      title: 'Target MU%',
      value: formatPercent(planning.summary.targetMargin || planning.financialPlan.marginTarget),
      titleTooltip: 'Target markup percentage used to plan margin and pricing.',
      onEdit: () => financialRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    {
      key: 'discount',
      title: 'Planned Discount',
      value: formatPercent(planning.financialPlan.plannedDiscountRate || 0),
      onEdit: () => financialRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    {
      key: 'looks',
      title: 'Planned Looks',
      value: derivedLooksTotal || plannedLooksTarget,
      titleTooltip: 'Total number of unique looks/styles planned for the year.',
      helper: hasLooksMismatch
        ? `${Math.abs(looksMismatch)} look${Math.abs(looksMismatch) === 1 ? '' : 's'} ${looksMismatch > 0 ? 'remaining' : 'over'}`
        : undefined,
      onEdit: () => productMixRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    {
      key: 'units',
      title: 'Planned Units',
      value: planning.summary.projectedUnits || 0,
      helper: hasUnitsMismatch
        ? `${Math.abs(unitsMismatch)} unit${Math.abs(unitsMismatch) === 1 ? '' : 's'} ${unitsMismatch > 0 ? 'remaining' : 'over'}`
        : undefined,
      onEdit: () => financialRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    {
      key: 'cogsBudget',
      title: 'Cost of Goods (COGS)',
      value: formatCurrency(totalCogsBudget),
      titleTooltip: 'Total production cost; includes shipping from manufacturer to you.',
      helper: `Per Look: ${formatCurrency(perLookCogsBudget)} • Per Unit: ${formatCurrency(perUnitCogsBudget)}`,
      onEdit: () => financialRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    {
      key: 'grossMarginBudget',
      title: 'Gross Margin Dollars',
      value: formatCurrency(totalGrossMargin),
      titleTooltip: 'Revenue minus cost of goods (COGS).',
      helper: `Per Look: ${formatCurrency(perLookGrossMargin)} • Per Unit: ${formatCurrency(perUnitGrossMargin)}`,
      onEdit: () => financialRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  ];

  const dropCycles = Array.isArray(planning.dropCycles) ? planning.dropCycles : [];
  const isCalendarOnly = variant === 'calendarOnly';

  const renderDropCalendarEditor = () => (
    <Card variant="outlined">
      <CardHeader
        title="Drop Cycle Calendar"
        subheader="Define the six-cycle rhythm so Design Studio can auto-fill launch & ship windows."
        action={
          <Button
            size="small"
            component={RouterLink}
            to={brandId ? `/brands/${brandId}/planning/calendar` : '#'}
          >
            Open Full Page
          </Button>
        }
      />
      <CardContent sx={{ maxHeight: 600, overflowY: 'auto', pr: 1 }}>
        {dropCycles.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No cycles configured yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {dropCycles.map((cycle, index) => {
              const windowLabel = describeCycleWindow(cycle);
              const directPercent = Math.round(
                (cycle?.channelFocus?.directToConsumer || 0) * 100
              );
              const sharePercent = Number.isFinite(cycle.revenueShare)
                ? Number((cycle.revenueShare * 100).toFixed(2))
                : '';
              return (
                <Paper key={cycle.cycleId || index} variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    flexWrap="wrap"
                    sx={{ mb: 2 }}
                  >
                    <Stack spacing={0.25}>
                      <Typography variant="subtitle1">
                        {cycle.label || `Drop Cycle ${index + 1}`}
                      </Typography>
                      {windowLabel && (
                        <Typography variant="caption" color="text.secondary">
                          {windowLabel}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={`${sharePercent}% Budget`} color="primary" />
                      <Tooltip title="Reset this cycle to defaults">
                        <IconButton onClick={() => handleResetDropCycle(index)} size="small">
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Cycle Label"
                        value={cycle.label || ''}
                        onChange={(event) =>
                          handleDropCycleFieldChange(index, 'label', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Cycle ID"
                        value={cycle.cycleId || ''}
                        onChange={(event) =>
                          handleDropCycleFieldChange(index, 'cycleId', event.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        select
                        label="Start Month"
                        value={cycle.startMonth || 1}
                        onChange={(event) =>
                          handleDropCycleFieldChange(index, 'startMonth', Number(event.target.value))
                        }
                        fullWidth
                      >
                        {MONTH_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        select
                        label="End Month"
                        value={cycle.endMonth || cycle.startMonth || 1}
                        onChange={(event) =>
                          handleDropCycleFieldChange(index, 'endMonth', Number(event.target.value))
                        }
                        fullWidth
                      >
                        {MONTH_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Revenue Share %"
                        type="number"
                        inputProps={{ min: 0, max: 100, step: 0.5 }}
                        value={sharePercent}
                        onChange={(event) =>
                          handleDropCycleRevenueShareChange(index, event.target.value)
                        }
                        fullWidth
                        helperText={`${sharePercent}% of annual budget allocated to this bimester`}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Product Mix for {cycle.label || 'This Bimester'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Configure which categories to focus on during this drop cycle
                      </Typography>
                      {(!cycle.productMix || cycle.productMix.length === 0) ? (
                        <Box sx={{ py: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            No categories configured for this bimester yet
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddBimesterCategory(index)}
                          >
                            Add Category
                          </Button>
                        </Box>
                      ) : (
                        <Stack spacing={1.5}>
                          {cycle.productMix.map((category, catIndex) => (
                            <Card key={`${index}-cat-${catIndex}`} variant="outlined" sx={{ bgcolor: 'background.default' }}>
                              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Grid container spacing={1.5} alignItems="center">
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      select
                                      label="Category"
                                      value={category.categoryId || ''}
                                      onChange={(e) => {
                                        const selected = DEFAULT_CATEGORY_OPTIONS.find(opt => opt.id === e.target.value);
                                        handleBimesterProductMixChange(index, catIndex, 'categoryId', e.target.value);
                                        handleBimesterProductMixChange(index, catIndex, 'categoryLabel', selected?.label || '');
                                      }}
                                      size="small"
                                      fullWidth
                                      SelectProps={{ native: true }}
                                    >
                                      <option value="">Select category</option>
                                      {DEFAULT_CATEGORY_OPTIONS.map(opt => (
                                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                                      ))}
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={6} sm={2}>
                                    <TextField
                                      label="Looks"
                                      type="number"
                                      value={category.plannedLooks || 0}
                                      onChange={(e) => handleBimesterProductMixChange(index, catIndex, 'plannedLooks', Number(e.target.value))}
                                      size="small"
                                      fullWidth
                                      InputProps={{ inputProps: { min: 0 } }}
                                    />
                                  </Grid>
                                  <Grid item xs={6} sm={2}>
                                    <TextField
                                      label="Units"
                                      type="number"
                                      value={category.plannedUnits || 0}
                                      onChange={(e) => handleBimesterProductMixChange(index, catIndex, 'plannedUnits', Number(e.target.value))}
                                      size="small"
                                      fullWidth
                                      InputProps={{ inputProps: { min: 0 } }}
                                    />
                                  </Grid>
                                  <Grid item xs={6} sm={2}>
                                    <TextField
                                      label="Share %"
                                      type="number"
                                      value={Math.round((category.plannedShare || 0) * 100)}
                                      onChange={(e) => handleBimesterProductMixChange(index, catIndex, 'plannedShare', Number(e.target.value) / 100)}
                                      size="small"
                                      fullWidth
                                      InputProps={{ inputProps: { min: 0, max: 100 } }}
                                    />
                                  </Grid>
                                  <Grid item xs={6} sm={2}>
                                    <TextField
                                      label="Category COGS Budget"
                                      type="number"
                                      value={(() => {
                                        const cycleShare = Number.isFinite(cycle.revenueShare)
                                          ? Math.max(cycle.revenueShare, 0)
                                          : 0;
                                        const categoryShare = Number.isFinite(category.plannedShare)
                                          ? Math.max(category.plannedShare, 0)
                                          : 0;
                                        const raw = totalCogsBudget * cycleShare * categoryShare;
                                        return Math.round(raw);
                                      })()}
                                      size="small"
                                      fullWidth
                                      InputProps={{
                                        readOnly: true,
                                        startAdornment: (
                                          <Typography sx={{ mr: 0.5, fontSize: '0.875rem' }}>$</Typography>
                                        )
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveBimesterCategory(index, catIndex)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          ))}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddBimesterCategory(index)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Add Another Category
                          </Button>
                        </Stack>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" justifyContent="flex-end">
                        <Button
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleRemoveDropCycle(index)}
                          size="small"
                        >
                          Remove Cycle
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        )}
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddDropCycle}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Add Drop Cycle
        </Button>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading financial data. This can take up to a minute.
        </Typography>
      </Box>
    );
  }

  const headerSection = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        gap: 2,
        flexWrap: 'wrap'
      }}
    >
      <Chip
        label={planning.status === 'committed' ? 'Committed Plan' : 'Draft Plan'}
        color={planning.status === 'committed' ? 'success' : 'default'}
        icon={<CheckCircleOutlineIcon />}
      />

      <Box display="flex" gap={1}>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={() => handleSave('draft')}
          disabled={saving}
        >
          Save Draft
        </Button>
        <Button
          variant="contained"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={() => handleSave('committed')}
          disabled={saving}
        >
          Commit Plan
        </Button>
      </Box>
    </Box>
  );

  const errorSection =
    errorMessages.length > 0 ? (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="error" gutterBottom>
            Please review the following issues:
          </Typography>
          <Stack spacing={1}>
            {errorMessages.map((message, index) => (
              <Typography key={index} variant="body2" color="error">
                • {message}
              </Typography>
            ))}
          </Stack>
        </CardContent>
      </Card>
    ) : null;

  const summarySection = null;

  const dirtyIndicator = dirty ? (
    <Box sx={{ mb: 2 }}>
      <LinearProgress color="secondary" />
      <Typography variant="caption" color="text.secondary">
        Unsaved changes
      </Typography>
    </Box>
  ) : null;

  const plannerContent = (
    <Grid container spacing={3}>
      {!isCalendarOnly && (
        <Grid item xs={12} lg={3} order={{ xs: 2, lg: 1 }}>
          <Grid container spacing={2}>
            {summaryCards.map((card) => (
              <Grid item xs={12} sm={6} lg={12} key={card.key}>
                <SummaryMetricCard {...card} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      <Grid item xs={12} lg={6} order={{ xs: 1, lg: 2 }}>
        <Stack spacing={3}>
          <Card variant="outlined" ref={financialRef}>
            <CardHeader
              title="Annual Financial Plan"
              subheader="Set annual revenue goals, margin targets, and default channel mix. Budget will be allocated across bimesters."
            />
            <CardContent>
              <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Revenue Goal"
                      type="number"
                      value={planning.financialPlan.revenueGoal}
                      onChange={(event) =>
                        handleFinancialChange('revenueGoal', Number(event.target.value))
                      }
                      fullWidth
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Projected Units"
                      type="number"
                      value={planning.summary.projectedUnits}
                      onChange={(event) =>
                        handleSummaryChange('projectedUnits', Number(event.target.value))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Target MU%
                      </Typography>
                      <Tooltip title="Target markup percentage used to plan margin and pricing.">
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Box>
                      </Tooltip>
                    </Box>
                    <Slider
                      value={Math.round(planning.financialPlan.marginTarget * 100)}
                      onChange={(_, value) =>
                        handleFinancialChange('marginTarget', Number(value) / 100)
                      }
                      valueLabelDisplay="auto"
                      sx={{ mt: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatPercent(planning.financialPlan.marginTarget)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Channel Mix (Direct-to-Consumer vs Wholesale)
                    </Typography>
                    <Slider
                      value={Math.round(
                        (planning.financialPlan.channelSplit.directToConsumer || 0) * 100
                      )}
                      onChange={(_, value) => handleChannelSplitChange(Number(value))}
                      valueLabelDisplay="auto"
                      sx={{ mt: 2 }}
                    />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        DTC {formatPercent(planning.financialPlan.channelSplit.directToConsumer)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Wholesale {formatPercent(planning.financialPlan.channelSplit.wholesale)}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Planned Discount %
                    </Typography>
                    <Slider
                      value={Math.round((planning.financialPlan.plannedDiscountRate || 0) * 100)}
                      onChange={(_, value) =>
                        handleFinancialChange('plannedDiscountRate', (Array.isArray(value) ? value[0] : value) / 100)
                      }
                      valueLabelDisplay="auto"
                      min={0}
                      max={80}
                      sx={{ mt: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatPercent(planning.financialPlan.plannedDiscountRate || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Planned Looks"
                      type="number"
                      value={planning.summary.plannedLooks}
                      onChange={(event) =>
                        handleSummaryChange('plannedLooks', Number(event.target.value))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={0.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Cost of Goods (COGS)
                        </Typography>
                        <Tooltip title="Includes shipping from manufacturer to you.">
                          <Box component="span" sx={{ display: 'inline-flex' }}>
                            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </Box>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Total: {formatCurrency(totalCogsBudget)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Per Look: {formatCurrency(perLookCogsBudget)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Per Unit: {formatCurrency(perUnitCogsBudget)}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={600}>
                        Gross Margin Dollars
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {formatCurrency(totalGrossMargin)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Per Look: {formatCurrency(perLookGrossMargin)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Per Unit: {formatCurrency(perUnitGrossMargin)}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      COGS Composition (Materials vs Labor)
                    </Typography>
                    <Slider
                      value={Math.round(materialsShare * 100)}
                      onChange={(_, value) =>
                        handleCostStructureChange(Array.isArray(value) ? value[0] : Number(value))
                      }
                      valueLabelDisplay="auto"
                      min={0}
                      max={100}
                      sx={{ mt: 2 }}
                    />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Materials {formatPercent(materialsShare)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Labor {formatPercent(laborShare)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Materials Budget: {formatCurrency(totalCogsBudget * materialsShare)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Labor Budget: {formatCurrency(totalCogsBudget * laborShare)}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Assumptions"
                      value={planning.financialPlan.assumptions}
                      onChange={(event) =>
                        handleFinancialChange('assumptions', event.target.value)
                      }
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder="Document key financial assumptions such as expected average order value, return rate, or promotional strategy."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {!isCalendarOnly && (
              <Card variant="outlined">
                <CardHeader
                  title="Video guide"
                  subheader="Quick walkthrough of the financial plan."
                  action={
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowVideoGuide((prev) => !prev)}
                      endIcon={showVideoGuide ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {showVideoGuide ? 'Hide' : 'Watch'}
                    </Button>
                  }
                />
                <Collapse in={showVideoGuide} timeout="auto" unmountOnExit>
                  <CardContent>
                    {planning.videoGuideUrl ? (
                      <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                        <Box
                          component="iframe"
                          title="Financial plan video guide"
                          src={planning.videoGuideUrl}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                            borderRadius: 1
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Video guide is not configured yet.
                      </Typography>
                    )}
                  </CardContent>
                </Collapse>
              </Card>
            )}

            {renderDropCalendarEditor()}
            <Card variant="outlined" ref={timelineRef}>
              <CardHeader
                title="Operational Timeline"
                subheader="Track milestones, owners, and dependencies."
                action={
                  <Button startIcon={<AddIcon />} size="small" onClick={handleAddMilestone}>
                    Add Milestone
                  </Button>
                }
              />
              <CardContent>
                {planning.timeline.length === 0 ? (
                  <Box
                    sx={{
                      py: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Add key milestones to build your drop plan checklist.
                    </Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddMilestone}>
                      Add Milestone
                    </Button>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {planning.timeline.map((milestone, index) => (
                      <Card key={`milestone-${index}`} variant="outlined">
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Milestone"
                                value={milestone.label || ''}
                                onChange={(event) =>
                                  handleTimelineChange(index, 'label', event.target.value)
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                label="Due Date"
                                type="date"
                                value={milestone.dueDate || ''}
                                onChange={(event) =>
                                  handleTimelineChange(index, 'dueDate', event.target.value)
                                }
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                label="Owner"
                                value={milestone.owner || ''}
                                onChange={(event) =>
                                  handleTimelineChange(index, 'owner', event.target.value)
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField
                                select
                                label="Status"
                                value={milestone.status || 'pending'}
                                onChange={(event) =>
                                  handleTimelineChange(index, 'status', event.target.value)
                                }
                                fullWidth
                                SelectProps={{ native: true }}
                              >
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                                <option value="done">Complete</option>
                              </TextField>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <TextField
                                  label="Dependencies"
                                  value={(milestone.dependencies || []).join(', ')}
                                  onChange={(event) =>
                                    handleTimelineChange(
                                      index,
                                      'dependencies',
                                      event.target.value
                                        .split(',')
                                        .map((item) => item.trim())
                                        .filter(Boolean)
                                    )
                                  }
                                  fullWidth
                                  placeholder="e.g. sample approval, photoshoot completion"
                                />
                                <IconButton
                                  aria-label="remove milestone"
                                  onClick={() => handleRemoveMilestone(index)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={3} order={{ xs: 3, lg: 3 }}>
          <Stack spacing={3}>
            <Card variant="outlined" ref={aiPromptRef}>
              <CardHeader
                title="AI Planner Assist"
                subheader="Get merchandising suggestions on demand."
              />
              <CardContent>
                <TextField
                  label="Ask the assistant"
                  placeholder='Example: "How do I reach 65% MU while keeping hoodies under $120 retail?"'
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={handleRequestSuggestion}
                    disabled={aiLoading}
                  >
                    Ask AI
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => setPrompt('')}
                    disabled={aiLoading || !prompt}
                  >
                    Clear
                  </Button>
                </Box>
                {aiLoading && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                    <Typography variant="caption" color="text.secondary">
                      Waiting for recommendation...
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardHeader title="Assumptions & Notes" />
              <CardContent>
                <TextField
                  placeholder="Capture rationale, cross-functional dependencies, or approvals needed."
                  value={planning.assumptions}
                  onChange={(event) =>
                    setPlanning((prev) => ({
                      ...prev,
                      assumptions: event.target.value
                    }))
                  }
                  multiline
                  minRows={6}
                  fullWidth
                />
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardHeader
                title="AI Recommendation Log"
                subheader="Track prompts, responses, and applied suggestions."
                avatar={<AutoAwesomeIcon color="primary" />}
              />
              <CardContent>
                {historyEntries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No AI activity yet. Ask the assistant to generate your first recommendation.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {historyEntries.map((entry, index) => (
                      <Box
                        key={`${entry.timestamp || index}`}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2">
                              {entry.prompt?.slice(0, 80) || 'Prompt'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.timestamp
                                ? formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })
                                : 'Just now'}
                            </Typography>
                          </Stack>
                          <Chip
                            size="small"
                            color={entry.applied ? 'success' : 'default'}
                            label={entry.applied ? 'Applied' : 'Not Applied'}
                          />
                        </Stack>
                        <Divider sx={{ my: 1.5 }} />
                        {entry.responseText && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-line', mb: 1.5 }}
                          >
                            {entry.responseText}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleApplySuggestion(entry)}
                            disabled={!entry.suggestedChanges || entry.applied}
                          >
                            Apply Suggestion
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
  );

  const calendarOnlyContent = (
    <Stack spacing={3}>
      {renderDropCalendarEditor()}
    </Stack>
  );

  return (
    <Box>
      {headerSection}
      {errorSection}
      {summarySection}
      {dirtyIndicator}
      {isCalendarOnly ? calendarOnlyContent : plannerContent}

      
          {/* --- Kam Assistant for Planner (floating bubble) --- */}
    <KamChatBox
      userId={brandId}
      sessionId={`planner-${brandId}`}
      context={{
        page: "planner",
        brandId,
        planner: plannerContext
      }}
    />
    </Box>
  );
};

export default BrandPlanningTab;
